import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getCommodities } from 'src/api/microservices/commodities'
import {
  GetCQGSymbolTenantCommodityMappingDetails,
  GetCQGSymbolTenantCommodityMappings,
  UpdateCQGSymbolTenantCommodityMapping,
} from 'src/api/microservices/trade-commodity-config'
import type {
  MappingDetails,
  MappingListItem,
  TenantCommodity,
  UpdateCommodityMappingRequest,
} from 'src/api/microservices/trade-commodity-config'
import { useCompanyContext } from 'src/companies/routes/company-context'

export type {
  CommodityMapping,
  MappingDetails,
  MappingListItem,
  MappingLocation,
  MappingStatus,
  TenantCommodity,
  UpdateCommodityMapping,
  UpdateCommodityMappingRequest,
} from 'src/api/microservices/trade-commodity-config'

export const TRADE_COMMODITY_MAPPINGS_QUERY_KEY = 'trade-commodity-mappings'
export const TRADE_COMMODITY_MAPPING_DETAILS_QUERY_KEY = 'trade-commodity-mapping-details'
export const TRADE_COMMODITY_UPDATE_MAPPING_MUTATION_KEY = 'trade-commodity-update-mapping'
export const TRADE_COMMODITIES_QUERY_KEY = 'trade-commodities-for-mapping'

export function useGetCQGSymbolTenantCommodityMappings() {
  const {
    company: { tenant_id: tenantId },
  } = useCompanyContext()

  return useQuery<MappingListItem[]>({
    queryKey: [TRADE_COMMODITY_MAPPINGS_QUERY_KEY, { tenantId }],
    queryFn: async () => {
      const [err, response] = await GetCQGSymbolTenantCommodityMappings(tenantId)
      if (err) throw err
      return response.mappings
    },
    enabled: !!tenantId,
  })
}

export function useGetCQGSymbolTenantCommodityMappingDetails(
  categorySymbol?: string,
  enabled = true
) {
  const {
    company: { tenant_id: tenantId },
  } = useCompanyContext()

  return useQuery<MappingDetails>({
    queryKey: [TRADE_COMMODITY_MAPPING_DETAILS_QUERY_KEY, { tenantId, categorySymbol }],
    queryFn: async () => {
      const [err, response] = await GetCQGSymbolTenantCommodityMappingDetails(
        { categorySymbol },
        tenantId
      )
      if (err) throw err
      return response.mapping
    },
    enabled: !!tenantId && !!categorySymbol && enabled,
    retry: false,
    gcTime: 0,
  })
}

export function useUpdateCQGSymbolTenantCommodityMapping() {
  const {
    company: { tenant_id: tenantId },
  } = useCompanyContext()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: [TRADE_COMMODITY_UPDATE_MAPPING_MUTATION_KEY, { tenantId }],
    mutationFn: async (data: UpdateCommodityMappingRequest) => {
      const [err] = await UpdateCQGSymbolTenantCommodityMapping(data, tenantId)
      if (err) throw err

      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [TRADE_COMMODITY_MAPPINGS_QUERY_KEY, { tenantId }],
        }),
        queryClient.invalidateQueries({
          queryKey: [
            TRADE_COMMODITY_MAPPING_DETAILS_QUERY_KEY,
            { tenantId, categorySymbol: data.categorySymbol },
          ],
        }),
      ])
    },
  })
}

export function useTenantCommodities({ tenantId, name = '' }: { tenantId: string; name?: string }) {
  return useQuery<TenantCommodity[]>({
    queryKey: [TRADE_COMMODITIES_QUERY_KEY, tenantId, name],
    queryFn: async () => {
      const [err, response] = await getCommodities({
        tenant_id: tenantId,
        continuationToken: null,
        name: name ?? undefined,
      })
      if (err) throw err
      return response.data.data
    },
    enabled: !!tenantId,
  })
}
